import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Contact() {
  const headerAnim = useScrollAnimation();
  const cardsAnim = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });
  const ctaAnim = useScrollAnimation({ rootMargin: '0px 0px -40px 0px' });

  const contactCards = [
    {
      icon: 'ri-mail-send-line',
      label: '이메일 문의',
      value: 'info@fixcope.com',
      description: '제품, 협력, 컨설팅 관련 문의를 이메일로 보내주세요.',
      action: 'mailto:info@fixcope.com',
      actionLabel: '이메일 보내기',
    },
    {
      icon: 'ri-phone-line',
      label: '전화 문의',
      value: '02-2264-2563',
      description: '평일 09:00 – 18:00 운영 (점심 12:00 – 13:00)',
      action: 'tel:0222642563',
      actionLabel: '전화 걸기',
    },
    {
      icon: 'ri-map-pin-2-line',
      label: '오시는 길',
      value: '서울특별시 송파구\n법원로 11길 7',
      description: '방문 상담은 사전 예약 후 진행됩니다.',
      action: 'https://maps.google.com/?q=서울특별시+송파구+법원로+11길+7',
      actionLabel: '지도 보기',
    },
  ];

  const inquiryAreas = [
    { icon: 'ri-heart-pulse-line', label: '의료장비 해외공급' },
    { icon: 'ri-eye-line', label: '비전 인스펙션 플랫폼' },
    { icon: 'ri-camera-3-line', label: '초소형 헤드분리카메라' },
    { icon: 'ri-global-line', label: '글로벌 진출 컨설팅' },
  ];

  return (
    <section id="contact" className="py-16 sm:py-20 md:py-28 bg-[#FFFBF5]">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div
          ref={headerAnim.ref}
          className={`text-center mb-16 transition-all duration-700 ${headerAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-8 h-[2px] bg-[#E8650A]"></span>
            <span className="text-xs font-semibold text-[#E8650A] uppercase tracking-widest whitespace-nowrap">Contact Us</span>
            <span className="w-8 h-[2px] bg-[#E8650A]"></span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A2530] mb-4">문의하기</h2>
          <p className="text-gray-500 text-[14px] sm:text-[15px] max-w-xl mx-auto leading-relaxed">
            Fixcope와의 협력, 제품 문의, 컨설팅 상담을 원하시면<br className="hidden sm:block" />
            아래 연락처로 직접 연락해 주세요.
          </p>
        </div>

        {/* Contact Cards */}
        <div
          ref={cardsAnim.ref}
          className={`grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 transition-all duration-700 ${cardsAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {contactCards.map((card, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-8 border border-gray-100 flex flex-col items-start group hover:border-[#E8650A]/30 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#E8650A]/10 mb-5 group-hover:bg-[#E8650A]/20 transition-colors">
                <i className={`${card.icon} text-xl text-[#E8650A]`}></i>
              </div>
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2 whitespace-nowrap">{card.label}</div>
              <div className="text-lg font-bold text-[#1A2530] mb-2 whitespace-pre-line leading-snug">{card.value}</div>
              <p className="text-[13px] text-gray-500 leading-relaxed mb-6 flex-1">{card.description}</p>
              <a
                href={card.action}
                target={card.action.startsWith('http') ? '_blank' : undefined}
                rel={card.action.startsWith('http') ? 'noopener noreferrer nofollow' : undefined}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1A2530] text-white text-sm font-medium hover:bg-[#E8650A] transition-colors whitespace-nowrap cursor-pointer"
              >
                {card.actionLabel}
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Bottom: Inquiry Areas + CTA */}
        <div
          ref={ctaAnim.ref}
          className={`bg-[#1A2530] rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-8 transition-all duration-700 ${ctaAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex-1">
            <div className="text-xs font-semibold text-[#E8650A] uppercase tracking-widest mb-3 whitespace-nowrap">문의 가능 분야</div>
            <div className="flex flex-wrap gap-3">
              {inquiryAreas.map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-sm text-[#FF8C3A]`}></i>
                  </div>
                  <span className="text-[13px] text-white whitespace-nowrap">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="shrink-0 text-left md:text-right">
            <p className="text-white/70 text-sm mb-1">빠른 연락을 원하신다면</p>
            <a
              href="tel:0222642563"
              className="text-2xl font-bold text-white hover:text-[#FF8C3A] transition-colors whitespace-nowrap cursor-pointer"
            >
              02-2264-2563
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
